import { Button, Card, CardGroup, Col, Form, LoaderOverlay } from "@emanprague/ppaskit";
import { Input } from "@frui.ts/bootstrap";
import PasswordInput from "controls/twoway/PasswordInput";
import { Router } from "@frui.ts/screens";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import LoginViewModel from "viewModels/profile/loginViewModel";
import PreAuthHeader from "components/PreAuthHeader";
import Tooltip from "@emanprague/ppaskit/dist/Tooltip";
import DisabledLoginReason from "components/DisabledLoginReason";
import OmniAuthButton from "components/OmniAuthButton";

const loginListView: ViewComponent<LoginViewModel> = observer(({ vm }) => {
  const ta = vm.localization.translateAttribute;
  const navigationLink = Router.getChildUrlFactory(vm.parent as any);

  if (vm.isLoginDisabled) {
    return (
      <>
        <PreAuthHeader translateFunc={vm.localization.translateGeneral} />
        <DisabledLoginReason
          content={
            vm.loginDisabledSetting?.value ??
            vm.localization.translateGeneral("admin.general_settings.disabled_login_reason.default_message")
          }
        />
      </>
    );
  }

  return (
    <>
      <PreAuthHeader translateFunc={vm.localization.translateGeneral} />
      <CardGroup>
        <h1 className="sr-only">{vm.translate("title")}</h1>
        <Card className="border-right-sm z-index-1">
          <LoaderOverlay loading={vm.busyWatcher.isBusy}>
            <Card.Body>
              <Form onSubmit={preventDefault(vm.login)}>
                <h2 className="display-1 text-center">{vm.translate("title")}</h2>

                <Form.Row>
                  <Form.Group as={Col}>
                    <Form.Label>{ta("user", "email")}</Form.Label>
                    <Input target={vm.credentials} property="email" autoFocus />
                  </Form.Group>
                </Form.Row>

                <Form.Row>
                  <Form.Group as={Col}>
                    <Form.Label>{ta("user", "password")}</Form.Label>
                    <PasswordInput
                      target={vm.credentials}
                      property="password"
                      feedbackInvalid={vm.errorMessage}
                      translateFunc={vm.translateGeneral}
                    />
                  </Form.Group>
                </Form.Row>

                <Button disabled={!vm.canLogin} type="submit" block={true}>
                  {vm.translate("login_button")}
                </Button>

                <Button
                  variant="link"
                  href={navigationLink("forgottenPassword")}
                  block={true}
                  className="font-weight-medium pb-sm-0">
                  {vm.translate("forgotten_password_button")}
                </Button>
              </Form>

              {vm.showOmniAuthButtons && (
                <>
                  <div className="d-flex mt-4 mb-2">
                    <hr className="my-auto flex-grow-1" />
                    <div className="px-4">{vm.translateGeneral("or")}</div>
                    <hr className="my-auto flex-grow-1" />
                  </div>

                  {vm.omniAuthErrorMessage && (
                    <Form.Control.Feedback className="mb-3" type="invalid">
                      {vm.omniAuthErrorMessage}
                    </Form.Control.Feedback>
                  )}

                  <div className="omniauth-buttons-container">
                    <OmniAuthButton
                      provider="google"
                      text={vm.translate("google_login")}
                      setting={vm.googleLoginSetting!}
                      origin="login"
                    />
                    <OmniAuthButton
                      provider="seznam"
                      text={vm.translate("seznam_login")}
                      setting={vm.seznamLoginSetting!}
                      origin="login"
                    />
                  </div>
                </>
              )}
            </Card.Body>
          </LoaderOverlay>
        </Card>
        <Card className="mb-0">
          <Card.Body className="text-center" style={{ alignContent: "center" }}>
            <h2 className="display-1 font-weight-light">{vm.translate("no_account_yet")}</h2>
            <p className="pt-0 pb-2 pt-sm-4 pt-md-5 pb-sm-4 pb-md-5 mb-md-3 px-2 px-md-0 w-75 w-sm-100 font-size-md mx-auto">
              {vm.translate("no_account_yet_hint")}
            </p>
            <Button variant="secondary" href={navigationLink("registration")} className="mb-3 mb-md-2">
              {vm.translate("register_button")}
            </Button>
            <Tooltip
              id="accountrequirementstooltip"
              placement="top"
              tooltip={
                <div
                  className="font-size-base"
                  dangerouslySetInnerHTML={{ __html: vm.translate("account_requirements_tooltip") }}
                />
              }>
              <Button variant="link" className="text-nowrap p-0 pt-1 mt-2 font-weight-medium pb-sm-0 d-block mx-auto cursor-help">
                {vm.translate("account_requirements_link")}
              </Button>
            </Tooltip>
          </Card.Body>
        </Card>
      </CardGroup>
    </>
  );
});

registerView(loginListView, LoginViewModel);
